// ConfirmEventModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../services/api';
import { trainingEventService } from '../../services/trainingEventService';
import './ConfirmEventModal.css';

// SVG иконки
const CalendarIcon = ({ width = 14, height = 14 }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const CheckCircleIcon = ({ width = 14, height = 14 }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const XIcon = ({ width = 14, height = 14 }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const UsersIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ConfirmEventModal = ({ 
  isOpen, 
  event, 
  onClose, 
  onSuccess,
  showToast 
}) => {
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState([]);
  const [completedDate, setCompletedDate] = useState('');

  // Форматирование даты
  const formatEventDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Установка даты завершения по умолчанию (сегодня)
  useEffect(() => {
    if (isOpen) {
      const today = new Date().toISOString().split('T')[0];
      setCompletedDate(today);
    }
  }, [isOpen]);

  // Загрузка участников мероприятия
  const loadParticipants = useCallback(async () => {
    if (!event?.id) return;
    
    setLoadingParticipants(true);
    
    try {
      const eventData = await trainingEventService.getById(event.id, true);
      const participantsList = eventData.participants || [];
      setParticipants(participantsList);
      setSelectedParticipantIds([]);
    } catch (err) {
      console.error('Ошибка загрузки участников мероприятия:', err);
      if (showToast) showToast('Не удалось загрузить участников мероприятия', 'error');
    } finally {
      setLoadingParticipants(false);
    }
  }, [event, showToast]);

  // Переключение выбора участника
  const toggleParticipantSelection = (participantId) => {
    if (confirming) return;
    setSelectedParticipantIds(prev => {
      if (prev.includes(participantId)) {
        return prev.filter(id => id !== participantId);
      } else {
        return [...prev, participantId];
      }
    });
  };

  // Выбрать всех участников
  const selectAll = () => {
    const allIds = participants.map(p => p.id);
    setSelectedParticipantIds(allIds);
  };

  // Снять выделение со всех
  const deselectAll = () => {
    setSelectedParticipantIds([]);
  };

  // Подтверждение мероприятия - отправляем POST запрос и удаляем участников
  const handleConfirm = async () => {
    if (selectedParticipantIds.length === 0) {
      if (showToast) showToast('Выберите хотя бы одного участника для подтверждения', 'error');
      return;
    }

    if (!completedDate) {
      if (showToast) showToast('Укажите дату завершения обучения', 'error');
      return;
    }
    
    setConfirming(true);
    
    try {
      // Получаем ID сотрудников из выбранных участников
      const selectedParticipants = participants.filter(p => selectedParticipantIds.includes(p.id));
      const employeeIds = selectedParticipants.map(p => p.employee_id);
      
      // Получаем course_id из мероприятия
      const courseId = event?.course_id;
      
      if (!courseId) {
        throw new Error('Не удалось определить курс мероприятия');
      }
      
      // 1. Отправляем POST запрос на bulk-update-expiration
      const response = await apiClient.post(
        '/api/trainings/bulk-update-expiration',
        {
          employee_ids: employeeIds,
          course_id: courseId,
          completed_date: completedDate,
          status: 'active'
        }
      );
      
      console.log('Bulk update response:', response);
      
      // 2. Удаляем выбранных участников из мероприятия
      await trainingEventService.removeParticipants(event.id, selectedParticipantIds);
      
      console.log(`Удалены участники из мероприятия: ${selectedParticipantIds.join(', ')}`);
      
      // 3. Проверяем, остались ли еще участники в мероприятии
      const updatedEventData = await trainingEventService.getById(event.id, true);
      const remainingParticipants = updatedEventData.participants || [];
      const remainingCount = remainingParticipants.length;
      
      const updatedCount = response?.data?.updated || 0;
      const createdCount = response?.data?.created || 0;
      const totalCount = updatedCount + createdCount;
      
      // 4. Если участников не осталось - удаляем мероприятие
      if (remainingCount === 0) {
        await trainingEventService.delete(event.id);
        console.log(`Мероприятие ${event.id} удалено, так как не осталось участников`);
        
        if (showToast) {
          showToast(
            `Обучение успешно обновлено для ${totalCount} сотрудников. Мероприятие удалено, так как не осталось участников.`,
            'success'
          );
        }
      } else {
        // Если участники остались - только уведомляем об обновлении
        if (showToast) {
          showToast(
            `Обучение успешно обновлено для ${totalCount} сотрудников. Участники удалены из мероприятия. Осталось участников: ${remainingCount}`,
            'success'
          );
        }
      }
      
      if (onSuccess) onSuccess();
      onClose();
      
    } catch (err) {
      console.error('Ошибка подтверждения мероприятия:', err);
      if (showToast) showToast(err.message || 'Не удалось обновить обучение', 'error');
    } finally {
      setConfirming(false);
    }
  };

  // Сброс состояния при закрытии
  const handleClose = () => {
    if (confirming) return;
    setParticipants([]);
    setSelectedParticipantIds([]);
    const today = new Date().toISOString().split('T')[0];
    setCompletedDate(today);
    onClose();
  };

  useEffect(() => {
    if (isOpen && event) {
      loadParticipants();
    }
  }, [isOpen, event, loadParticipants]);

  if (!isOpen) return null;

  const totalParticipants = participants.length;
  const selectedCount = selectedParticipantIds.length;

  return (
    <div className="confirm-modal-overlay" onClick={handleClose}>
      <div className="confirm-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-modal-header">
          <h3>
            <ClockIcon />
            Подтверждение мероприятия
          </h3>
          <button className="confirm-modal-close" onClick={handleClose} disabled={confirming}>
            <XIcon />
          </button>
        </div>
        
        <div className="confirm-modal-body">
          {/* Информация о мероприятии */}
          <div className="confirm-event-info">
            <div className="confirm-event-title">{event?.title || 'Без названия'}</div>
            <div className="confirm-event-meta">
              <span className="flex items-center gap-1">
                <CalendarIcon width={12} height={12} />
                {formatEventDate(event?.start_date)} — {formatEventDate(event?.end_date)}
              </span>
              <span className="flex items-center gap-1">
                <UsersIcon />
                Участников: {totalParticipants}
              </span>
            </div>
          </div>

          {/* Блок выбора даты завершения */}
          <div className="confirm-date-section">
            <label className="confirm-date-label">
              Дата завершения обучения:
              <input
                type="date"
                className="confirm-date-input"
                value={completedDate}
                onChange={(e) => setCompletedDate(e.target.value)}
                disabled={confirming}
              />
            </label>
            <p className="confirm-date-hint">
              Эта дата будет установлена как дата прохождения обучения для выбранных участников
            </p>
          </div>
          
          {/* Список участников */}
          <div className="confirm-participants-section">
            <div className="confirm-participants-header">
              <div className="confirm-participants-title">
                Участники мероприятия
              </div>
              <div className="confirm-participants-actions">
                <button 
                  className="btn-secondary btn-sm"
                  onClick={selectAll}
                  disabled={confirming || totalParticipants === 0}
                >
                  Выбрать всех
                </button>
                <button 
                  className="btn-secondary btn-sm"
                  onClick={deselectAll}
                  disabled={confirming || selectedCount === 0}
                >
                  Снять выбор
                </button>
              </div>
            </div>
            
            {loadingParticipants ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Загрузка участников...</p>
              </div>
            ) : participants.length === 0 ? (
              <div className="participants-empty">
                <p>Нет участников для подтверждения</p>
              </div>
            ) : (
              <div className="participants-list">
                {participants.map(participant => {
                  const isSelected = selectedParticipantIds.includes(participant.id);
                  
                  return (
                    <div 
                      key={participant.id} 
                      className={`participant-confirm-item ${isSelected ? 'selected' : ''}`}
                    >
                      {/* Чекбокс выбора */}
                      <div className="participant-confirm-checkbox">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleParticipantSelection(participant.id)}
                          disabled={confirming}
                        />
                      </div>
                      
                      {/* Информация об участнике */}
                      <div className="participant-confirm-info">
                        <div className="participant-confirm-row">
                          <div className="participant-confirm-name">
                            {participant.employee_name}
                            {participant.personnel_number && (
                              <span className="participant-confirm-personnel">
                                {participant.personnel_number}
                              </span>
                            )}
                            {participant.position && (
                              <span className="participant-confirm-position">
                                {participant.position}
                              </span>
                            )}
                            {participant.department && (
                              <span className="participant-confirm-department">
                                {participant.department}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Статус участника */}
                      <div className="participant-confirm-status">
                        <span className="status-badge status-ready">Готов к подтверждению</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        
        <div className="confirm-modal-footer">
          <button 
            className="btn-secondary" 
            onClick={handleClose}
            disabled={confirming}
          >
            Отмена
          </button>
          <button 
            className="btn-primary" 
            onClick={handleConfirm}
            disabled={confirming || selectedCount === 0 || loadingParticipants || !completedDate}
          >
            {confirming ? (
              <>
                <div className="btn-spinner"></div>
                Подтверждение...
              </>
            ) : (
              <>
                <CheckCircleIcon />
                Подтвердить ({selectedCount})
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmEventModal;